import React, { useEffect, useState } from "react";
import { styled } from "@mui/system";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import LineOfBusinessModal from "./lineOfBusinessModal"; // Import the modal component
import { matchLineOfBusinesses } from "../functions/matchLineOfBusinesses";
import { useDispatch } from "react-redux";
import { setDialog, setHPConfigChanged, setLoading } from "slices/appSlice";
import Api from "api";
import { formErrorStrfromObj } from "utils/shared";

const CustomTypography = styled((props) => (
  <Typography variant="body1" {...props} />
))({
  minWidth: "130px",
});

const ActiveLineOfBusiness = ({ formData, setFormData, editable }) => {
  const [openDialog, setOpenDialog] = useState(false); // Dialog open state
  const [dialogMode, setDialogMode] = useState(""); // To handle 'add', 'view', 'edit'
  const [selectedItem, setSelectedItem] = useState(null); // Selected item for view/edit
  const [lobItems, setLobItems] = useState(formData.lobConfigurations || []);
  const [initialLobItems] = useState(formData.lobConfigurations || []);
  const [matchingItems, setMatchingItems] = useState([]);
  const [isSecondaryEnabled, setIsSecondaryEnabled] = useState(false);

  const [newLobConfigKey, setNewLobConfigKey] = useState(-1);
  const dispatch = useDispatch();

  const lobMappingType = [
    { id: 0, value: " " },
    { id: 1, value: "Claim Number" },
    { id: 2, value: "Group ID" },
    { id: 3, value: "ID Card" },
    { id: 4, value: "Line of Business" },
    { id: 5, value: "Funding Status Code" },
  ];

  const [lobConfigOptions, setLobConfigOptions] = useState([]);

  const fetchLobConfigOptions = async () => {
    try {
      dispatch(setLoading(true));
      const response = await Api.Lob.lobValueList();
      const options = response.data.map((item) => ({
        id: item.lineOfBusinessConfigValueReferenceKey,
        value: item.lineOfBusinessConfigValue,
      }));
      setLobConfigOptions(options);
    } catch (error) {
      const message =
        formErrorStrfromObj(error) || "An error occurred during the fetch.";
      dispatch(setDialog({ message, title: "Error" }));
    } finally {
      dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    fetchLobConfigOptions();
  }, []);
  // Handle secondary switch change
  const handleSecondarySwitchChange = (isEnabled) => {
    setIsSecondaryEnabled(isEnabled);
  };

  const hasSecondaryValues = (item) =>
    Boolean(
      item?.secondaryType?.trim() ||
        item?.secondaryFormatText?.trim() ||
        item?.secondaryConfigValue?.trim() ||
        item?.secondaryConfig2ndValue?.trim()
    );

  const handleOpenDialog = (mode, item = null) => {
    setDialogMode(mode);
    if (mode === "add") {
      setSelectedItem({
        lobConfigKey: "",
        lobDisplayName: "",
        primaryType: "",
        primaryFormatText: "",
        primaryConfigValue: "",
        primaryConfig2ndValue: "",
        secondaryType: "",
        secondaryFormatText: "",
        secondaryConfigValue: "",
        secondaryConfig2ndValue: "",
      });
      setIsSecondaryEnabled(false);
    } else if (mode === "view") {
      setSelectedItem(item);
      let matches = matchLineOfBusinesses(item, formData.lineOfBusinesses); // Use the matching logic function
      setMatchingItems(matches);
      setIsSecondaryEnabled(hasSecondaryValues(item));
    } else if (mode === "edit" && item) {
      setSelectedItem(item);
      setIsSecondaryEnabled(hasSecondaryValues(item));
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // setSelectedItem(null);
    setMatchingItems([]);
  };

  const handleSave = () => {
    const isDuplicate = lobItems.some(
      (item) =>
        item.lobDisplayName.toLowerCase() ===
          selectedItem.lobDisplayName.toLowerCase() &&
        item.lobConfigKey !== selectedItem.lobConfigKey
    );

    if (isDuplicate) {
      alert(
        "Entered DisplayName must be unique. This value already exists. " +
          selectedItem.lobDisplayName
      );
      return;
    }

    let nextLobConfigKey = newLobConfigKey - 1;
    let newItem = {
      lobConfigKey: selectedItem.lobConfigKey || nextLobConfigKey, // Unique ID for new item
      lobDisplayName: selectedItem.lobDisplayName || "",
      primaryType: selectedItem.primaryType || "",
      primaryFormatText: selectedItem.primaryFormatText || "", // Default option for "startsWith"
      primaryConfigValue: selectedItem.primaryConfigValue || "",
      primaryConfig2ndValue: selectedItem.primaryConfig2ndValue || "",
      deleteItem: false,
    };
    setNewLobConfigKey(nextLobConfigKey);

    // Add secondary values if secondary switch is enabled
    if (isSecondaryEnabled) {
      newItem = {
        ...newItem,
        secondaryType: selectedItem.secondaryType || "",
        secondaryFormatText: selectedItem.secondaryFormatText || "",
        secondaryConfigValue: selectedItem.secondaryConfigValue || "",
        secondaryConfig2ndValue: selectedItem.secondaryConfig2ndValue || "",
      };
    }
    if (dialogMode === "add") {
      const updatedLobItems = [...lobItems, newItem];
      setLobItems(updatedLobItems);
      setFormData({
        ...formData,
        lobConfigurations: updatedLobItems,
      });
      dispatch(setHPConfigChanged({ hpConfigChanged: true }));
    } else if (dialogMode === "edit") {
      setLobItems((prevLobItems) => {
        const updatedLobItems = prevLobItems.map((item) => {
          if (item.lobConfigKey === selectedItem.lobConfigKey) {
            return { ...newItem };
          }
          return item;
        });

        // Update formData with the new lobItems state
        setFormData({
          ...formData,
          lobConfigurations: updatedLobItems,
        });

        return updatedLobItems;
      });
      dispatch(setHPConfigChanged({ hpConfigChanged: true }));
    }
    handleCloseDialog();
  };

  const handleDelete = (lobConfigKey) => {
    // setLobItems(lobItems.filter((item) => item.lobConfigKey !== lobConfigKey));
    const updatedLobItems = lobItems.map((item) =>
      item.lobConfigKey === lobConfigKey ? { ...item, deleteItem: true } : item
    );

    setLobItems(updatedLobItems);
    setFormData({
      ...formData,
      lobConfigurations: updatedLobItems, // Updated items with deleteItem set to true
    });
    dispatch(setHPConfigChanged({ hpConfigChanged: true }));
  };

  const handleReload = () => {
    setLobItems(initialLobItems); // Reset lobItems to initial data
  };

  return (
    <Box sx={{ flex: 1, width: "50%" }}>
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <CustomTypography fontWeight="bold">
          Active Line of Business Values:
        </CustomTypography>
        {editable && (
          <Box>
            <Tooltip title="Add a new LOB configuration">
              <Button
                variant="text"
                sx={{ fontWeight: "bold" }}
                onClick={() => handleOpenDialog("add")}
              >
                ADD
              </Button>
            </Tooltip>
            <Tooltip title="This will remove all your changes and load the initial data">
              <Button
                variant="text"
                sx={{ fontWeight: "bold" }}
                onClick={handleReload} // Reload button handler
              >
                RELOAD
              </Button>
            </Tooltip>
          </Box>
        )}
      </Stack>
      {/* List */}
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: "4px",
          padding: 0,
          marginTop: 0,
          maxHeight: "350px",
          minHeight: "350px",
          overflowY: "auto",
        }}
      >
        <List dense>
          {lobItems
            .filter((item) => !item.deleteItem)
            .map((item) => (
              <ListItem
                key={item.lobConfigKey}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingY: 0.5,
                }}
              >
                <ListItemText
                  primary={`${item.lobDisplayName}`}
                  sx={{ margin: 0 }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ minWidth: "auto", padding: 0 }}
                    onClick={() => handleOpenDialog("view", item)}
                  >
                    VIEW
                  </Button>
                  {editable && (
                    <Button
                      variant="text"
                      size="small"
                      sx={{ minWidth: "auto", padding: 0 }}
                      onClick={() => handleOpenDialog("edit", item)}
                    >
                      EDIT
                    </Button>
                  )}
                  {editable && (
                    <Button
                      variant="text"
                      size="small"
                      sx={{ minWidth: "auto", padding: 0 }}
                      onClick={() => handleDelete(item.lobConfigKey)}
                    >
                      DELETE
                    </Button>
                  )}
                </Box>
              </ListItem>
            ))}
        </List>
      </Box>
      {/* Modal for Add, View, Edit */}
      <LineOfBusinessModal
        editable={editable}
        open={openDialog}
        onClose={handleCloseDialog}
        mode={dialogMode}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        handleSave={handleSave}
        matchingItems={matchingItems}
        lobMappingType={lobMappingType}
        lobConfigOptions={lobConfigOptions}
        handleSecondarySwitchChange={handleSecondarySwitchChange}
      />
    </Box>
  );
};
export default ActiveLineOfBusiness;
